import React from "react";
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import type { AuditScoreRange } from "@/hooks/useConversationsInsights";
import { ChevronDown } from "lucide-react";
import QualityScoreIcon from '../../../public/icons/new-dashboard/file-check-02.svg';

type Props = {
  /** `{ min, max }` or `undefined` when no filter applied */
  value: AuditScoreRange | undefined;
  /** Bubble the new object (or `undefined`) upward */
  onChange: (range: AuditScoreRange | undefined) => void;
};

export default function QualityScore({ value, onChange }: Props) {
  const [open, setOpen] = React.useState(false);

  const qsMin = value?.min ?? "";
  const qsMax = value?.max ?? "";

  /** helper to update just one side */
  function setPart(part: "min" | "max", v: number | null) {
    if (v === null) {
      // clear that side
      const other = part === "min" ? value?.max : value?.min;
      if (other == null) onChange(undefined);
      else
        onChange(
          part === "min"
            ? { min: 0, max: other } // min cleared -> treat as 0
            : { min: other, max: 100 }, // max cleared -> treat as 100
        );
    } else {
      const newRange: AuditScoreRange = {
        min: part === "min" ? v : value?.min ?? 0,
        max: part === "max" ? v : value?.max ?? 100,
      };
      onChange(newRange);
    }
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="text-muted">
          <QualityScoreIcon className="mr-2" />
          {value ? `${value.min}-${value.max}%` : "Quality Score"}
          <ChevronDown size={16} color="#71717A" className="ml-2" />
        </Button>
      </PopoverTrigger>

      <PopoverContent className="w-56 space-y-3">
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="text-xs text-muted-foreground">Min</label>
            <Input
              type="number"
              min={0}
              max={100}
              value={qsMin}
              onChange={(e) =>
                setPart(
                  "min",
                  e.target.value ? Number(e.target.value) : null,
                )
              }
            />
          </div>
          <div>
            <label className="text-xs text-muted-foreground">Max</label>
            <Input
              type="number"
              min={0}
              max={100}
              value={qsMax}
              onChange={(e) =>
                setPart(
                  "max",
                  e.target.value ? Number(e.target.value) : null,
                )
              }
            />
          </div>
        </div>

        <div className="flex justify-between">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onChange(undefined)}
          >
            Reset
          </Button>
          <Button variant="default" size="sm" onClick={() => setOpen(false)}>
            Apply
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}